import { Inject, Injectable } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { Section } from '../models/section';
import { Data } from '../models/data';
import { ValidationRule, ValidationRuleToken } from '../models/validation-rules';
import { noValidate } from '../utils/no-validate';

export enum SectionValidatorListenOnType {
  OnCreate = 'onCreate',
  OnUpdate = 'onUpdate',
}

/**
 * @deprecated do not use
 */
@Injectable()
export class SectionValidatorService {
  constructor(
    @Inject(ValidationRuleToken) private injectedRules: ValidationRule[],
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}
  /**
   * Returns array of errors (string[]) based on ValidityRules configured
   * in given section applied to given record object
   * @param record
   * @param section
   * @param activeListener
   *
   * @returns errors string[]
   */
  findErrors(record: Data, section: Partial<Section>, activeListener: 'onCreate' | 'onUpdate'): string[] {
    const { validityRules } = section;
    if (validityRules) {
      const contextualErrors =
        activeListener === SectionValidatorListenOnType.OnCreate
          ? this.applyValidityRules(record, validityRules.onCreate)
          : this.applyValidityRules(record, validityRules.onUpdate);
      const onSaveErrors = this.applyValidityRules(record, validityRules.onSave, section);
      // return all found errors (general first, then contextualized errors)
      return [...onSaveErrors, ...contextualErrors];
    }
    // return empty
    return [];
  }

  /**
   * Evaluates the record against all validity rules passed
   * @param record
   * @param rules
   */
  private applyValidityRules(
    record: Data,
    rules: Array<ValidationRule | string>,
    section?: Partial<Section>
  ): string[] {
    return (rules || [])
      .map(ruleDef => {
        const rule = typeof ruleDef === 'string' ? this.findRule(ruleDef) : ruleDef;
        return rule(record, section);
      })
      .filter(Boolean);
  }

  /**
   * Attempts to find a rule by name within the injectedRules provided by ValidationRuleToken
   * else return console warns rule not found and return a wildcard function that always return null
   * (no validates)
   * @param ruleName
   */
  private findRule(ruleName: string) {
    const rule = this.injectedRules.find(r => r.name.includes(ruleName));
    if (!rule) {
      this.logger.warn(
        `DEVELOPER WARNING: Unable to find rule "${ruleName}", assigning temporal rule with no validation, make sure you are assigning the right name OR you provided the service`
      );
      return noValidate;
    }
    return rule;
  }

  /**
   * Returns boolean value based on ValidityRules configured
   * in given section applied to given record object
   * @param record
   * @param section
   * @param activeListener
   *
   * @returns boolean
   */
  isValid(record: Data, section: Partial<Section>, isCreate: boolean) {
    const errors = this.findErrors(
      record,
      section,
      isCreate ? SectionValidatorListenOnType.OnCreate : SectionValidatorListenOnType.OnUpdate
    );
    return Array.isArray(errors) && errors.length === 0;
  }
}
